import { Octokit } from '@octokit/core';
import { getUserDetails } from './gitAuth';
import { Buffer } from 'buffer';
import { useGlobalStore } from '@/modules/globalModule/store';
import { processCustomPythonNode } from '@/modules/codeViewModule/customCodeProcessing/processPythonNode';
import {
  processCustomCppNode,
  processCustomHppText
} from '@/modules/codeViewModule/customCodeProcessing/processCppNode';
import {
  processCustomCmakeLists,
  processCustomLaunchFile,
  processCustomPackageXml
} from '@/modules/codeViewModule/customCodeProcessing/processCustomPackageText';
import {
  processCustomMsgText,
  processCustomSrvText
} from '@/modules/codeViewModule/customCodeProcessing/processCustomInterface';
import { Node } from '@/modules/nodesCanvasModule/components/storeClasses/node';
import { PackageT } from '@/modules/nodesCanvasModule/components/storeClasses/packageT';
import { Message } from '@/modules/nodesCanvasModule/components/storeClasses/message';
import { Service } from '@/modules/nodesCanvasModule/components/storeClasses/service';
import { toGitRepoName } from '@/modules/globalModule/components/helperMethods';
interface GitTreeObject {
  download_url: string;
  git_url: string;
  html_url: string;
  name: string;
  path: string;
  sha: string;
  size: number;
  type: string;
  url: string;
  fullPath?: string;
  _links: {
    git: string;
    html: string;
    self: string;
  };
}
interface GitTreeChild {
  path: string;
  mode?: string;
  type?: string;
  size?: number;
  sha?: string;
  url?: string;
  pkgName?: string;
  fullPath?: string;
}

export const gitPull = async (
  token: string,
  repoName: string,
  workspaceId: string
) => {
  repoName = toGitRepoName(repoName);
  const octokit = new Octokit({
    auth: token
  });
  const userDetails = await getUserDetails(token);
  const owner = userDetails.login;
  const res = await octokit.request(
    `GET /repos/${owner}/${repoName}/contents`,
    {
      owner: owner,
      repo: repoName,
      path: '',
      headers: {
        'X-GitHub-Api-Version': '2022-11-28'
      }
    }
  );
  const fullTree = await getFullTree(
    token,
    userDetails.login,
    repoName,
    res.data
  );
  for (const file of fullTree) {
    const fileContent = await getBlobContent(token, owner, repoName, file);
    saveGitFile(workspaceId, file, file.fullPath || file.path, fileContent);
  }
};

// https://docs.github.com/en/rest/git/trees?apiVersion=2022-11-28#get-a-tree
const getTree = async (
  token: string,
  owner: string,
  repoName: string,
  treeSha: string
) => {
  const octokit = new Octokit({
    auth: token
  });

  const res = await octokit.request(
    `GET /repos/${owner}/${repoName}/git/trees/${treeSha}`,
    {
      owner: owner,
      repo: repoName,
      tree_sha: treeSha,
      headers: {
        'X-GitHub-Api-Version': '2022-11-28'
      }
    }
  );
  return res.data;
};
const getFullTree = async (
  token: string,
  owner: string,
  repoName: string,
  treeFiles: GitTreeObject[]
): Promise<Array<GitTreeChild | GitTreeObject>> => {
  const files: Array<GitTreeChild | GitTreeObject> = treeFiles.filter(
    (treeFile) => treeFile.type === 'file'
  );
  let dirs: Array<GitTreeChild | GitTreeObject> = treeFiles.filter(
    (treeFile) => treeFile.type === 'dir'
  );
  while (dirs.length > 0) {
    const newDirs: Array<GitTreeChild | GitTreeObject> = [];
    for (let dir of dirs) {
      if (!dir.sha) continue;
      const tree = await getTree(token, owner, repoName, dir.sha);
      for (const treeChild of tree.tree) {
        treeChild.fullPath = dir.fullPath
          ? `${dir.fullPath}/${treeChild.path}`
          : `${dir.path}/${treeChild.path}`;
        if (treeChild.type === 'tree') {
          newDirs.push(treeChild);
        } else if (treeChild.type === 'blob') {
          files.push(treeChild);
        }
      }
    }
    dirs = newDirs;
  }

  return files;
};

const getBlobContent = async (
  token: string,
  owner: string,
  repoName: string,
  treeFile: GitTreeChild
): Promise<string> => {
  const octokit = new Octokit({
    auth: token
  });
  const res = await octokit.request(
    `GET /repos/${owner}/${repoName}/git/blobs/${treeFile.sha}`,
    {
      owner: owner,
      repo: repoName,
      file_sha: treeFile.sha,
      headers: {
        'X-GitHub-Api-Version': '2022-11-28'
      }
    }
  );

  return Buffer.from(res.data.content, 'base64').toString();
};

/**
 * Given a file and its contents, this function determines which object
 * to attach the custom code to, and saves that code to persistance
 * @param workspaceId
 * @param file
 * @param fileContent
 * @returns
 */
const saveGitFile = (
  workspaceId: string,
  file: GitTreeChild,
  filePath: string,
  fileContent: string
) => {
  if (!file.path) return;
  const globalStore = useGlobalStore();
  const elem = globalStore.getElemFromFilePath(filePath);
  elem;
  if (!elem) {
    console.warn(
      `Unable to save file: could not find element from filepath: ${filePath}`
    );
    return;
  }
  const elemType = globalStore.getTypeOfElemById(elem.id);
  if (elemType === 'Node') {
    const newNode = elem as Node;
    if (filePath.endsWith('.py')) {
      newNode.customText = processCustomPythonNode(
        newNode,
        fileContent,
        workspaceId
      );
    }
    if (filePath.endsWith('.cpp')) {
      newNode.customText = processCustomCppNode(
        newNode,
        fileContent,
        workspaceId
      );
    } else if (filePath.endsWith('.hpp')) {
      newNode.customText = processCustomHppText(newNode, fileContent);
    }
  } else if (elemType === 'Message') {
    const msg = elem as Message;
    msg.customText = processCustomMsgText(msg, fileContent);
  } else if (elemType === 'Service') {
    const srv = elem as Service;
    srv.customText = processCustomSrvText(srv, fileContent);
  } else {
    const pkg = elem as PackageT;
    if (!pkg) {
      console.warn('Error saving custom code: Package not found');
      return;
    }
    if (filePath.endsWith('.launch.py')) {
      pkg.customText = processCustomLaunchFile(pkg, fileContent);
    } else if (filePath.endsWith('package.xml')) {
      pkg.customText = processCustomPackageXml(pkg, fileContent);
    } else if (filePath.endsWith('CMakeLists.txt')) {
      pkg.customText = processCustomCmakeLists(pkg, fileContent);
    }
  }
  globalStore.saveData();
};
